import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Select,
  MenuItem,
  Link,
  SelectChangeEvent,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useAuth } from '../context/AuthContext'
import { useThemeMode, ThemePreference } from '../context/ThemeContext'
import { useSettings } from '../pages/Settings/use-settings'
import {
  MovieceptionSwitch,
  MovieceptionPrimaryButton,
  MovieceptionOutlinedButton,
} from './Buttons/movieception-buttons'
import { SettingsResponse } from '../client/settings-api'

const ModalContainer = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 12,
    padding: theme.spacing(2),
  },
}))

const Header = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
}))

const Title = styled(Typography)({
  fontSize: 24,
  fontWeight: 700,
})

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: 15,
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
}))

const Row = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  padding: theme.spacing(1.5, 0),
}))

const RowLabel = styled(Typography)({
  fontSize: 15,
  fontWeight: 500,
})

const RowNote = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  fontStyle: 'italic',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(-0.5),
  marginBottom: theme.spacing(0.5),
}))

const Actions = styled(DialogActions)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2, 1, 1),
}))

const ButtonRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
}))

const SkipLink = styled(Link)(({ theme }) => ({
  alignSelf: 'center',
  fontSize: 13,
  color: theme.palette.text.secondary,
  cursor: 'pointer',
})) as typeof Link

const isThemePreference = (value: string): value is ThemePreference =>
  value === 'light' || value === 'dark' || value === 'system'

export const ONBOARDING_MODAL_PREVIEW_EVENT = 'movieception:preview-onboarding'

export const OnboardingModal = () => {
  const { isAuthenticated } = useAuth()
  const { preference, setPreference } = useThemeMode()
  const { settings, setSettings, updateRemote } = useSettings()
  const navigate = useNavigate()

  const [hasBeenDismissedThisSession, setHasBeenDismissedThisSession] =
    useState(false)

  const [isOpenForAdminPreview, setIsOpenForAdminPreview] = useState(false)

  useEffect(() => {
    const handlePreviewRequest = () => {
      setHasBeenDismissedThisSession(false)
      setIsOpenForAdminPreview(true)
    }
    window.addEventListener(
      ONBOARDING_MODAL_PREVIEW_EVENT,
      handlePreviewRequest,
    )
    return () =>
      window.removeEventListener(
        ONBOARDING_MODAL_PREVIEW_EVENT,
        handlePreviewRequest,
      )
  }, [])

  const shouldShowFromFirstLogin =
    isAuthenticated &&
    !!settings &&
    settings.onboardingCompleted === false &&
    !hasBeenDismissedThisSession

  const shouldShowOnboardingModal =
    shouldShowFromFirstLogin || isOpenForAdminPreview

  const persistSettingsAndMarkOnboardingComplete = (
    settingsPatchFromCurrentValues: Partial<SettingsResponse>,
    navigateToRouteAfterClose?: string,
  ) => {
    setHasBeenDismissedThisSession(true)

    if (isOpenForAdminPreview) {
      setIsOpenForAdminPreview(false)
      return
    }

    updateRemote({
      ...settingsPatchFromCurrentValues,
      onboardingCompleted: true,
    })
    if (navigateToRouteAfterClose) navigate(navigateToRouteAfterClose)
  }

  const handleThemePreferenceChange = (event: SelectChangeEvent) => {
    const selectedThemePreference = event.target.value
    if (!isThemePreference(selectedThemePreference) || !settings) return
    setPreference(selectedThemePreference)
    setSettings({ ...settings, theme: selectedThemePreference })
  }

  const handleEmailNotificationsToggle = (isEmailEnabled: boolean) => {
    if (!settings) return
    setSettings({
      ...settings,
      notifications: {
        ...settings.notifications,
        emailNotifications: isEmailEnabled,
      },
    })
  }

  const handleSpoilerControlToggle = (isSpoilerControlEnabled: boolean) => {
    if (!settings) return
    setSettings({ ...settings, spoilerControl: isSpoilerControlEnabled })
  }

  const handleProfileVisibilityChange = (event: SelectChangeEvent) => {
    if (!settings) return
    setSettings({ ...settings, profileVisibility: event.target.value })
  }

  if (!shouldShowOnboardingModal || !settings) return null

  const buildSettingsPatchFromCurrentValues =
    (): Partial<SettingsResponse> => ({
      theme: preference,
      profileVisibility: settings.profileVisibility,
      spoilerControl: settings.spoilerControl,
      notifications: settings.notifications,
    })

  return (
    <ModalContainer open={shouldShowOnboardingModal} maxWidth='sm' fullWidth>
      <DialogContent>
        <Header>
          <Title>Welcome to Movieception!</Title>
          <Subtitle>Let's get you set up</Subtitle>
        </Header>

        <Row>
          <RowLabel>Emails</RowLabel>
          <MovieceptionSwitch
            checked={settings.notifications.emailNotifications}
            onChange={(e) => handleEmailNotificationsToggle(e.target.checked)}
          />
        </Row>
        <RowNote>
          Toggles the basics, pick exactly what hits your inbox in Settings
        </RowNote>

        <Row>
          <RowLabel>Theme</RowLabel>
          <Select
            size='small'
            value={preference}
            onChange={handleThemePreferenceChange}
          >
            <MenuItem value='light'>Light</MenuItem>
            <MenuItem value='dark'>Dark</MenuItem>
            <MenuItem value='system'>System</MenuItem>
          </Select>
        </Row>

        <Row>
          <RowLabel>Spoiler control</RowLabel>
          <MovieceptionSwitch
            checked={settings.spoilerControl}
            onChange={(e) => handleSpoilerControlToggle(e.target.checked)}
          />
        </Row>
        <RowNote>
          Hides upcoming episode images and descriptions in season details
        </RowNote>

        <Row>
          <RowLabel>Profile</RowLabel>
          <Select
            size='small'
            value={settings.profileVisibility}
            onChange={handleProfileVisibilityChange}
          >
            <MenuItem value='public'>Public</MenuItem>
            <MenuItem value='private'>Private</MenuItem>
          </Select>
        </Row>
      </DialogContent>

      <Actions>
        <ButtonRow>
          <MovieceptionOutlinedButton
            label='Guide'
            onClick={() =>
              persistSettingsAndMarkOnboardingComplete(
                buildSettingsPatchFromCurrentValues(),
                '/guide',
              )
            }
          />
          <MovieceptionOutlinedButton
            label='Settings'
            onClick={() =>
              persistSettingsAndMarkOnboardingComplete(
                buildSettingsPatchFromCurrentValues(),
                '/profile/settings',
              )
            }
          />
          <MovieceptionPrimaryButton
            label='Explore!'
            onClick={() =>
              persistSettingsAndMarkOnboardingComplete(
                buildSettingsPatchFromCurrentValues(),
                '/discover',
              )
            }
          />
        </ButtonRow>
        <SkipLink
          component='button'
          onClick={() =>
            persistSettingsAndMarkOnboardingComplete(
              buildSettingsPatchFromCurrentValues(),
            )
          }
        >
          skip for now
        </SkipLink>
      </Actions>
    </ModalContainer>
  )
}

export default OnboardingModal
